import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../../../services/api';
import { useDemoStore } from '../../../../store/demoFlowStore';
import { ChevronDown, ChevronUp, Plus, ArrowLeft, Truck, Clock, CheckCircle2, Store, PackageCheck, Check, Eye, Edit } from 'lucide-react';
import { showSuccessMessage } from '../../../../utils/toast';
// import { QuotationForm } from '../../../estimations/components/quotation_form';
import { EstimationScreen as OrderingScreen } from '../../../customer_stages/estimation/screen/estimation_screen';
import { OrderDetails } from '../components/order_details';
import { PaymentDetails } from '../components/payment_details';
import { BookEntry } from '../../book_entry/components/book_entry';
import { TrackOrder } from '../../track_order/components/track_order';

const STAGES = [
  { id: 'Pending', icon: Clock },
  { id: 'Approved', icon: CheckCircle2 },
  { id: 'Sent to store', icon: Store },
  { id: 'Dispatched', icon: Truck },
  { id: 'Received', icon: PackageCheck }
];

export const WorkspacePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { leads, estimations, orders, payments, updateEstimation, updateOrder, addPayment } = useDemoStore();

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'Cash' });

  const { data: serverCustomers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      try {
        const response = await api.get('/customers');
        return response.data || [];
      } catch (err) {
        return [];
      }
    }
  });

  const { data: customerEstimation, refetch: refetchEstimation } = useQuery({
    queryKey: ['estimation', id],
    queryFn: async () => {
      try {
        const response = await api.get(`/estimations/customer/${id}`);
        return response.data?.data || null;
      } catch (err) {
        return null;
      }
    },
    enabled: !!id
  });

  const handleAddPayment = (e) => {
    e.preventDefault();
    if (!paymentForm.amount) return;
    addPayment({
      customer: lead.name,
      orderId: leadOrders[0]?._id,
      amount: '₹' + paymentForm.amount,
      method: paymentForm.method,
      date: new Date().toLocaleDateString('en-GB')
    });
    setPaymentForm({ amount: '', method: 'Cash' });
    setShowPaymentModal(false);
    showSuccessMessage('Payment successfully saved!');
  };

  const stateCustomer = location.state?.customer;
  const storeLead = leads.find(l => l._id === id || l.id === id);
  const serverLead = serverCustomers.find(c => c._id === id);
  const lead = stateCustomer || serverLead || storeLead || { name: 'Unknown Customer', _id: id };
  const exactLeadEstimations = estimations.filter(e => e.leadId === id);
  const isFallbackEstimation = exactLeadEstimations.length === 0 && estimations.length > 0;
  const leadEstimations = isFallbackEstimation ? [estimations[0]] : exactLeadEstimations;

  const exactLeadOrders = orders.filter(o => o.leadId === id);
  const leadOrders = exactLeadOrders.length > 0 ? exactLeadOrders : (
    isFallbackEstimation ? orders.filter(o => o.leadId === estimations[0].leadId) : []
  );

  const leadPayments = payments.filter(p => p.customer === (lead.customerName || lead.name) || p.orderId === leadOrders[0]?._id);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [isPaymentDone, setIsPaymentDone] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const isTrackOnly = searchParams.get('tab') === 'track_order';
  const [activeScreen, setActiveScreen] = useState(isTrackOnly ? 'track_order' : 'estimations'); // null, 'estimations', 'orders', 'dispatch', 'payments'

  const hasCompletedPayment = isPaymentDone || (leadPayments && leadPayments.length > 0);

  const totalValue = leadOrders.reduce((sum, ord) => sum + parseInt(ord.amount.replace(/[^0-9]/g, '') || 0), 0) ||
    leadEstimations.reduce((sum, est) => sum + parseInt(est.amount.replace(/[^0-9]/g, '') || 0), 0);

  const totalReceived = leadPayments.reduce((sum, pay) => sum + parseInt(pay.amount.replace(/[^0-9]/g, '') || 0), 0);

  return (
    <div className="min-h-screen bg-transparent p-0 md:p-0 font-sans">
      {/* Header and Cards Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2 mt-2">
        {/* Left: Name and Title */}
        <div className="flex items-center gap-4 shrink-0">
          <button onClick={() => navigate(-1)} className="p-2 bg-white border border-slate-200 rounded-full hover:bg-slate-50 hover:border-slate-300 active:bg-slate-100 active:scale-95 transition-all shadow-sm">
            <ArrowLeft size={16} className="text-slate-600" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900 tracking-tight leading-tight">
                {lead.customerName || `${lead.firstName || ""} ${lead.lastName || ""}`.trim() || lead.name || 'Customer'}
              </h1>
              {lead.sdId && (
                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[11px] font-bold border border-blue-200 uppercase">
                  {lead.sdId}
                </span>
              )}
              {(lead.customerType || lead.type) && (
                <span className="px-2.5 py-0.5 bg-slate-100 text-slate-700 rounded-full text-[11px] font-semibold border border-slate-200 capitalize">
                  {lead.customerType || lead.type}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-[12px] text-slate-500 font-medium">
              {(lead.phoneNumber || lead.phone) && <span>Phone: <strong className="text-slate-700 font-semibold">{lead.phoneNumber || lead.phone}</strong></span>}
              {(lead.email || lead.customerEmail) && <span>• Email: <strong className="text-slate-700 font-semibold">{lead.email || lead.customerEmail}</strong></span>}
            </div>
          </div>
        </div>

        {/* Right: Small Compact Cards */}
        <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar pb-1">
          {!isTrackOnly && (
            <>
              <button
                onClick={() => setActiveScreen(activeScreen === 'estimations' ? null : 'estimations')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 shrink-0 active:scale-95 ${activeScreen === 'estimations'
                    ? 'bg-black text-white border-black shadow-md hover:bg-gray-900'
                    : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-slate-700 shadow-sm active:bg-gray-100'
                  }`}
              >
                <div className={activeScreen === 'estimations' ? 'text-white' : 'text-slate-500'}>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </div>
                <h3 className="text-[13px] font-semibold tracking-tight">Estimation</h3>
              </button>

              <button
                onClick={() => setActiveScreen(activeScreen === 'orders' ? null : 'orders')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 shrink-0 active:scale-95 ${activeScreen === 'orders'
                    ? 'bg-black text-white border-black shadow-md hover:bg-gray-900'
                    : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-slate-700 shadow-sm active:bg-gray-100'
                  }`}
              >
                <div className={activeScreen === 'orders' ? 'text-white' : 'text-slate-500'}>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                </div>
                <h3 className="text-[13px] font-semibold tracking-tight">Order</h3>
              </button>

              <button
                onClick={() => {
                  setActiveScreen(activeScreen === 'dispatch' ? null : 'dispatch');
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 shrink-0 ${activeScreen === 'dispatch'
                      ? 'bg-black text-white border-black shadow-md hover:bg-gray-900 active:scale-95'
                      : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-slate-700 shadow-sm active:bg-gray-100 active:scale-95'
                  }`}
              >
                <div className={activeScreen === 'dispatch' ? 'text-white' : 'text-slate-500'}>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8" /></svg>
                </div>
                <h3 className="text-[13px] font-semibold tracking-tight">Book Entry</h3>
              </button>

              <button
                onClick={() => setActiveScreen(activeScreen === 'payments' ? null : 'payments')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 shrink-0 active:scale-95 ${activeScreen === 'payments'
                    ? 'bg-black text-white border-black shadow-md hover:bg-gray-900'
                    : 'bg-bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-slate-700 shadow-sm active:bg-gray-100'
                  }`}
              >
                <div className={activeScreen === 'payments' ? 'text-white' : 'text-slate-500'}>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                </div>
                <h3 className="text-[13px] font-semibold tracking-tight">Payments</h3>
              </button>
            </>
          )}

          {isTrackOnly && (
            <button
              onClick={() => setActiveScreen(activeScreen === 'track_order' ? null : 'track_order')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 shrink-0 active:scale-95 ${activeScreen === 'track_order'
                  ? 'bg-black text-white border-black shadow-md hover:bg-gray-900'
                  : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-slate-700 shadow-sm active:bg-gray-100'
                }`}
            >
              <div className={activeScreen === 'track_order' ? 'text-white' : 'text-slate-500'}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" /></svg>
              </div>
              <h3 className="text-[13px] font-semibold tracking-tight">Track Order</h3>
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {activeScreen && (
          <div className="flex flex-col lg:flex-row gap-8 animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="flex-1">
              <div className="rounded-2xl overflow-hidden">
                {activeScreen === 'estimations' && (
                  <div className="p-4 animate-in fade-in slide-in-from-bottom-4 duration-300 w-full rounded-xl">
                    <OrderingScreen
                      embedded={true}
                      initialData={{ leadId: id, customer: lead }}
                      onBack={() => setActiveScreen(null)}
                      onSuccess={(newOrder) => {
                        setPlacedOrder(newOrder);
                        setActiveScreen('orders');
                      }}
                    />
                  </div>
                )}

                {activeScreen === 'orders' && (
                  <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 w-full">
                    <OrderDetails
                      ordersList={Array.isArray(customerEstimation) ? customerEstimation : (customerEstimation ? [customerEstimation] : [])}
                      initialOrderId={placedOrder?._id}
                      onPaymentComplete={() => {
                        setIsPaymentDone(true);
                        refetchEstimation();
                      }}
                    />
                  </div>
                )}

                {activeScreen === 'track_order' && (
                  <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 w-full">
                    <TrackOrder 
                      customerId={id}
                      orderId={placedOrder?._id || leadOrders[0]?._id}
                      estimationId={customerEstimation?._id}
                    />
                  </div>
                )}

                {activeScreen === 'dispatch' && (
                  <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 w-full">
                    <BookEntry 
                      ordersList={Array.isArray(customerEstimation) ? customerEstimation : (customerEstimation ? [customerEstimation] : [])}
                      initialOrderId={placedOrder?._id}
                      lead={lead} 
                    />
                  </div>
                )}

                {activeScreen === 'payments' && (
                  <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 w-full">
                    <PaymentDetails 
                      customerId={id}
                      ordersList={Array.isArray(customerEstimation) ? customerEstimation : (customerEstimation ? [customerEstimation] : [])}
                      initialOrderId={placedOrder?._id}
                      lead={lead} 
                    />
                  </div>
                )}
              </div>
            </div>


          </div>
        )}
      </div>
      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Add Payment</h2>
            <form onSubmit={handleAddPayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹)</label>
                <input
                  required
                  type="number"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  placeholder="e.g. 5000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Method</label>
                <select
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  value={paymentForm.method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="UPI">UPI</option>
                  <option value="Card">Card</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                >
                  Save Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};




